/**
 * The phone's read layer.
 *
 * Two rules from docs/02 §8 shape everything here:
 *
 *  1. **Every screen reads the LOCAL mirror, never the network.** These hooks
 *     subscribe to Dexie and nothing else. There is no fetch in this directory.
 *  2. **Nothing reads the ambient clock.** `useNow` takes a `TrustedClock` and
 *     is the only place a phone screen learns what time it is. A component that
 *     called `Date.now()` would render an hour out on a tablet with stale
 *     tzdata and nobody would be able to tell why (docs/02 §4).
 *
 * `lib/repo.ts` exposes a `useQuery` hook with the same intent, but it
 * subscribes via `db.on('changes')` — the dexie-observable addon API, which
 * Dexie 4.4.5 does not have; calling it throws `Cannot read properties of
 * undefined (reading 'subscribe')` and takes the whole screen down. This uses
 * Dexie 4's own `liveQuery` instead. Swap back once repo.ts is fixed; the
 * shapes are deliberately compatible.
 *
 * It lives under `chores/` rather than `phone/` because `phone/` itself belongs
 * to another owner; `proposals/` imports it from here.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { liveQuery } from 'dexie'
import type { OfficeDb } from '../../lib/db.js'
import type { MutationContext } from '../../lib/mutations.js'
import { DEFAULT_SETTINGS, type MemberId, type TrustedClock } from '@office/domain'

export type Row = Record<string, unknown>

/** Stable empty arrays: a fresh `[]` as `initial` would re-render forever. */
export const NO_ROWS: readonly Row[] = Object.freeze([])
const NO_MEMBERS: readonly MemberLite[] = Object.freeze([])

// ---------------------------------------------------------------------------
// Row coercion
//
// The mirror holds whatever the server sent: snake_case columns (docs/01), and
// timestamps that may be ISO strings (which is what `mutations.ts` writes) or
// epoch numbers (which is what the domain wants). Coercing at this boundary
// keeps every `as` cast and every `Date.parse` in one place instead of
// scattered through the components.
// ---------------------------------------------------------------------------

export function asText(v: unknown, fallback = ''): string {
  if (typeof v === 'string') return v
  if (v === null || v === undefined) return fallback
  return String(v)
}

/** Null for absent/blank/unparseable, so callers cannot mistake 0 for "missing". */
export function asMs(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string' && v.trim() !== '') {
    const t = Date.parse(v)
    return Number.isNaN(t) ? null : t
  }
  return null
}

export function asNum(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return fallback
}

export function asBool(v: unknown, fallback = false): boolean {
  if (typeof v === 'boolean') return v
  if (v === 'true' || v === 1) return true
  if (v === 'false' || v === 0) return false
  return fallback
}

/** A nullable id column. Empty string counts as absent — `mutations.ts` writes ''. */
export function asIdOrNull<T extends string>(v: unknown): T | null {
  if (v === null || v === undefined) return null
  const s = asText(v)
  return s === '' ? null : (s as T)
}

export function asTextOrNull(v: unknown): string | null {
  if (v === null || v === undefined) return null
  const s = asText(v)
  return s === '' ? null : s
}

export function asStringList(v: unknown): string[] {
  return Array.isArray(v) ? v.map(x => asText(x)) : []
}

export function asRecord(v: unknown): Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
    ? { ...(v as Record<string, unknown>) }
    : {}
}

/** Read a column under any of the given names, first hit wins. */
export function column(row: Row, ...names: string[]): unknown {
  for (const n of names) {
    const v = row[n]
    if (v !== undefined) return v
  }
  return undefined
}

// ---------------------------------------------------------------------------
// Live reads
// ---------------------------------------------------------------------------

/**
 * Subscribe to a query over the local mirror.
 *
 * `run` must read TABLES ONLY and close over nothing that changes: everything
 * derived from props belongs in a `useMemo` over the rows this returns. That
 * keeps the subscription stable for the component's lifetime and makes a stale
 * closure impossible.
 */
export function useLive<T>(
  db: OfficeDb,
  run: (db: OfficeDb) => Promise<T>,
  initial: T,
  /**
   * Changing this tears the subscription down and opens a new one. Needed when
   * the query reads a *different table* than before: `liveQuery` tracks the
   * tables the last run touched, so without a re-subscribe it would keep
   * waking on the old one.
   */
  resubscribeKey?: string,
): T {
  const [value, setValue] = useState<T>(initial)
  const runRef = useRef(run)

  // Declared BEFORE the subscription effect so that on mount the ref is already
  // current, and on every later render the observable picks up the newest `run`
  // without tearing down the subscription.
  useEffect(() => {
    runRef.current = run
  })

  useEffect(() => {
    let alive = true
    const sub = liveQuery(() => runRef.current(db)).subscribe({
      next: v => {
        if (alive) setValue(v)
      },
      // A read that throws must not take the screen down with it. The mirror is
      // the only read path, so the honest fallback is "keep showing what we had".
      error: () => {},
    })
    return () => {
      alive = false
      sub.unsubscribe()
    }
  }, [db, resubscribeKey])

  return value
}

/** Every row of one mirrored table, kept live. */
export function useTable(db: OfficeDb, table: keyof OfficeDb & string): readonly Row[] {
  return useLive(
    db,
    async d =>
      (await (d as unknown as Record<string, { toArray(): Promise<Row[]> }>)[table]!.toArray()),
    NO_ROWS,
    table,
  )
}

/**
 * The corrected time, refreshed on an interval.
 *
 * The initial value is read lazily from the injected clock, so the very first
 * paint is already on office time. Pass `intervalMs: 0` to freeze it — which is
 * what a test with an injected clock wants.
 */
export function useNow(clock: TrustedClock, intervalMs = 30_000): number {
  const [now, setNow] = useState<number>(() => clock.now())

  useEffect(() => {
    setNow(clock.now())
    if (!Number.isFinite(intervalMs) || intervalMs <= 0) return
    const id = setInterval(() => setNow(clock.now()), intervalMs)
    return () => clearInterval(id)
  }, [clock, intervalMs])

  return now
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export interface MemberLite {
  id: MemberId
  name: string
  role: string
  /** Owners vote on proposals; interns do not (docs/01 §1). */
  isOwner: boolean
  /**
   * Whether this person is in the pool chores rotate through, and therefore
   * whether a swap may be handed to them at all (docs/01 §1.1, §3.2). Absent
   * column means yes: a member is rotation-eligible until someone says not.
   */
  rotationEligible: boolean
}

export function useMembers(db: OfficeDb): readonly MemberLite[] {
  return useLive(
    db,
    async d => {
      const [people, members] = await Promise.all([d.people.toArray(), d.members.toArray()])
      const names = new Map<string, string>()
      for (const p of people) names.set(asText(p['id']), asText(p['display_name']))
      const out: MemberLite[] = members.map(m => {
        const id = asText(column(m, 'person_id', 'id'))
        const role = asText(m['role'], 'owner')
        return {
          id: id as MemberId,
          name: names.get(id) ?? id,
          role,
          isOwner: role === 'owner',
          rotationEligible: asBool(column(m, 'rotation_eligible', 'rotationEligible'), true),
        }
      })
      return out.sort((a, b) => a.name.localeCompare(b.name)) as readonly MemberLite[]
    },
    NO_MEMBERS,
  )
}

/** Display name for an id, or the id itself when the person is not mirrored yet. */
export function nameOf(members: readonly MemberLite[], id: MemberId | null): string {
  if (id === null) return 'anyone'
  return members.find(m => m.id === id)?.name ?? String(id)
}

/** "You" for the phone's owner, otherwise the display name. */
export function nameOrYou(members: readonly MemberLite[], id: MemberId | null, me: MemberId): string {
  return id === me ? 'You' : nameOf(members, id)
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export interface PhoneSettings {
  dayRolloverHour: number
  weekStartDow: number
  autoApproveHours: number
  purchaseThreshold: number
}

export const PHONE_SETTINGS_DEFAULTS: PhoneSettings = {
  dayRolloverHour: DEFAULT_SETTINGS.dayRolloverHour,
  weekStartDow: DEFAULT_SETTINGS.weekStartDow,
  autoApproveHours: DEFAULT_SETTINGS.autoApproveHours,
  purchaseThreshold: DEFAULT_SETTINGS.purchaseThreshold,
}

/** Tolerant of snake_case or camelCase, because only the server decides. */
export function toPhoneSettings(row: Row | undefined): PhoneSettings {
  if (row === undefined) return PHONE_SETTINGS_DEFAULTS
  return {
    dayRolloverHour: asNum(
      column(row, 'day_rollover_hour', 'dayRolloverHour'),
      PHONE_SETTINGS_DEFAULTS.dayRolloverHour,
    ),
    weekStartDow: asNum(
      column(row, 'week_start_dow', 'weekStartDow'),
      PHONE_SETTINGS_DEFAULTS.weekStartDow,
    ),
    autoApproveHours: asNum(
      column(row, 'auto_approve_hours', 'autoApproveHours'),
      PHONE_SETTINGS_DEFAULTS.autoApproveHours,
    ),
    purchaseThreshold: asNum(
      column(row, 'purchase_threshold', 'purchaseThreshold'),
      PHONE_SETTINGS_DEFAULTS.purchaseThreshold,
    ),
  }
}

export function useSettings(db: OfficeDb): PhoneSettings {
  const rows = useTable(db, 'settings')
  return useMemo(() => toPhoneSettings(rows[0]), [rows])
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * The context every `mutations.*` call needs. `now` is the trusted clock, never
 * `Date.now`, so a queued mutation carries office time even on a skewed device.
 */
export function useMutationContext(db: OfficeDb, clock: TrustedClock, me: MemberId): MutationContext {
  return useMemo(() => ({ db, now: () => clock.now(), actorId: String(me) }), [db, clock, me])
}

// ---------------------------------------------------------------------------
// Tone
//
// docs/01 §3.4 and docs/03: overdue chores are AMBER. Red is reserved for
// genuine errors. Encoding the mapping as data rather than as a class name in
// twelve places makes "chores never go red" a property a test can assert.
// ---------------------------------------------------------------------------

export type Tone = 'neutral' | 'good' | 'warn' | 'accent'

export interface ToneVars {
  fg: string
  bg: string
  border: string
}

export const TONES: Readonly<Record<Tone, ToneVars>> = Object.freeze({
  neutral: { fg: 'var(--text)', bg: 'var(--surface)', border: 'var(--border)' },
  good: { fg: 'var(--ok)', bg: 'var(--ok-soft)', border: 'var(--ok)' },
  /** Overdue. Amber, never red — the board celebrates, it does not accuse. */
  warn: { fg: 'var(--warn)', bg: 'var(--warn-soft)', border: 'var(--warn)' },
  accent: { fg: 'var(--accent)', bg: 'var(--accent-soft)', border: 'var(--accent)' },
})

/** Inline style for a tone. Only ever emits tokens from tokens.css. */
export function toneStyle(tone: Tone): { color: string; background: string; borderColor: string } {
  const t = TONES[tone]
  return { color: t.fg, background: t.bg, borderColor: t.border }
}
